import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, MessageSquarePlus, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ReviewForm } from './ReviewForm';
import type { Database } from '@/integrations/supabase/types';

type Review = Database['public']['Tables']['reviews']['Row'];

export const Reviews = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  return (
    <section id="reviews" className="py-24 bg-[#F5F1E8]">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="text-left">
            <motion.span 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-accent tracking-[0.15em] text-sm font-bold mb-4 block font-clean uppercase"
            >
              Guest Reviews
            </motion.span>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-serif text-emerald"
            >
              What Our Guests Say
            </motion.h2>
          </div>

          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-8 py-4 bg-emerald text-ivory rounded-full font-clean font-bold tracking-widest hover:bg-emerald/90 transition-all duration-300 shadow-lg text-sm uppercase"
          >
            <MessageSquarePlus className="w-5 h-5" />
            Write a Review
          </motion.button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white p-8 rounded-xl border border-accent/10 h-64 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl border border-accent/10 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col"
              >
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={14}
                      className={`${
                        i < review.rating ? "text-accent fill-accent" : "text-gray-200"
                      } mr-1`}
                    />
                  ))}
                </div>

                <h3 className="text-emerald font-clean font-bold text-lg mb-0.5">{review.name}</h3>
                <p className="text-xs text-[#59635C]/60 font-clean mb-4 uppercase tracking-wider">
                  {new Date(review.created_at || '').toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
                
                <p className="text-[#59635C] font-clean leading-relaxed text-base flex-grow">
                  "{review.review_text}"
                </p>

                {review.photos && review.photos.length > 0 && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {review.photos.map((photo, pIdx) => (
                      <button
                        key={pIdx}
                        onClick={() => setSelectedImage(photo)}
                        className="w-12 h-12 rounded-lg overflow-hidden border border-accent/10 hover:border-accent transition-colors"
                      >
                        <img src={photo} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mt-16">
          <Link
            to="/reviews"
            className="px-10 py-4 border border-emerald text-emerald rounded-full font-clean font-bold tracking-widest hover:bg-emerald/5 transition-all duration-300 text-sm uppercase flex items-center gap-2"
          >
            View More Reviews
            <ChevronRight className="w-4 h-4" />
          </Link>
          <motion.a
            href="https://wa.me/918900992020"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-10 py-4 bg-emerald text-ivory rounded-full font-clean font-bold tracking-widest hover:bg-emerald/90 transition-all duration-300 shadow-lg text-sm uppercase"
          >
            PLAN YOUR STAY
          </motion.a>
        </div>
      </div>

      {/* Review Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowForm(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-2xl"
            >
              <ReviewForm 
                onClose={() => setShowForm(false)} 
            onSuccess={() => {
              fetchReviews();
              // After approval we could show a message, but since it's pending it won't show yet.
            }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-6 right-6 text-ivory p-2 hover:bg-white/10 rounded-full transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={selectedImage}
              alt="Review photo"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};